import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Checkbox,
  Text,
  Input,
  Spinner,
  Badge,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular, Search20Regular } from "@fluentui/react-icons";
import {
  CreateTasksRequest,
  createTasks,
} from "../../../services/tasks.service";
import {
  TaskDefinitionDto,
  TaskDefinitionVisibility,
  getTaskDefinitions,
} from "../../../services/task-definitions.service";
import { uploadWorkbookSnapshot } from "../../../services/workbook-upload.service";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { useTypographyStyles } from "../../../styles/typography.styles";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

const useStyles = makeStyles({
  dialogSurface: {
    // width: min(720px, 95vw) so the dialog stretches out in standalone /
    // wide task panes but falls back gracefully when the task pane is narrow.
    // maxHeight caps at 90vh — the inner list scrolls internally so long
    // agent lists don't push the dialog past the screen.
    width: "min(720px, 95vw)",
    maxWidth: "720px",
    minWidth: "380px",
    maxHeight: "90vh",
  },
  // Search bar pinned above the scrolling list so it stays visible no matter
  // how many results are returned.
  searchRow: {
    paddingBottom: tokens.spacingVerticalS,
  },
  // The list scroll area. Capped so ~260px of dialog chrome (title bar,
  // search row, action footer, padding) always stays on screen.
  resultsArea: {
    maxHeight: "calc(90vh - 260px)",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  // Section header sits above each visibility group; empty groups are
  // hidden entirely so we don't burn real estate on "My agents (0)".
  sectionHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  sectionAgentList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalXS,
  },
  // Card-style row. Border on the left goes brand-coloured when selected;
  // the whole card is clickable to toggle. Description lets users pick
  // agents by purpose, not just by name.
  agentCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeftWidth: "3px",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  agentCardSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandForeground1,
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  agentCardCheckbox: {
    // Fluent's Checkbox has some baked-in top padding; nudge up so it aligns
    // with the semibold name in the row.
    marginTop: "2px",
  },
  agentCardBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  agentCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  agentCardName: {
    fontWeight: tokens.fontWeightSemibold,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },
  // Two-line clamp on descriptions — long ones fade out gracefully rather
  // than push the card tall or spill sideways.
  agentCardDescription: {
    color: tokens.colorNeutralForeground3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  agentCardDescriptionEmpty: {
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
  categoryBadge: {
    flexShrink: 0,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalL,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: "center",
  },
  // Prereqs step.
  stepIntro: {
    display: "block",
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  prereqList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxHeight: "calc(90vh - 260px)",
    overflowY: "auto",
  },
  prereqItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke3,
  },
  prereqCheckboxRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalXS,
  },
  prereqDescription: {
    display: "block",
    marginLeft: "28px",
    color: tokens.colorNeutralForeground3,
  },
  prereqAppliesTo: {
    display: "block",
    marginLeft: "28px",
    marginTop: "2px",
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },
  // Confirm step.
  confirmList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    maxHeight: "calc(90vh - 260px)",
    overflowY: "auto",
  },
  confirmRow: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  confirmRowDescription: {
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Display order for the three accordion sections.
 * "mine" = visibility==="private" AND the caller owns it.
 */
const VISIBILITY_ORDER: Array<{ key: "system" | "office" | "mine"; label: string }> = [
  { key: "system", label: "Standard agents" },
  { key: "office", label: "Shared with my office" },
  { key: "mine", label: "My agents" },
];

interface CreateTaskDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  spreadsheetId: string;
  onSuccess: () => void;
}

/**
 * Modal for running AI tasks. Fetches every task definition the caller can
 * see (their own + office-shared + system) and renders them as a dense,
 * search-filterable, accordion-grouped checkbox list — built to scale to
 * dozens of definitions without becoming unmanageable.
 */
const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  isOpen,
  onDismiss,
  spreadsheetId,
  onSuccess,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { userInfo, isStandalone } = useWorkflowIntelligence();
  const callerEmail = userInfo?.mail?.toLowerCase() ?? "";
  const track = useTrackEvent();

  const [definitions, setDefinitions] = useState<TaskDefinitionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Saga state — the dialog walks the user through select → prereqs → confirm.
  // The prereqs step auto-skips when the aggregated prereq list is empty (see
  // `handleNext` and `handleBack` below).
  type Step = "select" | "prereqs" | "confirm";
  const [step, setStep] = useState<Step>("select");
  // Keyed by normalized-lowercase title so identical prereqs from different
  // agents share a single checkbox (users don't tick "aodata populated"
  // three times just because K1 / D1 / C1 all need it).
  const [checkedPrereqs, setCheckedPrereqs] = useState<Set<string>>(new Set());

  // Re-fetch each time the dialog opens. Definitions can change between opens
  // (the user may have created or edited one elsewhere), so we don't cache.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    getTaskDefinitions()
      .then((data) => {
        if (!cancelled) setDefinitions(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(getUserFriendlyErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Filter + bucket in one pass. Search matches against name, description, or
  // category — case-insensitive substring. Buckets are kept even when empty so
  // the section headers always show correct counts.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const buckets: Record<"system" | "office" | "mine", TaskDefinitionDto[]> = {
      system: [],
      office: [],
      mine: [],
    };
    for (const def of definitions) {
      if (q) {
        const hay = [
          def.name,
          def.description ?? "",
          def.category ?? "",
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) continue;
      }
      if (def.visibility === "system") {
        buckets.system.push(def);
      } else if (def.visibility === "office") {
        buckets.office.push(def);
      } else if (def.visibility === "private" && def.ownerEmail?.toLowerCase() === callerEmail) {
        buckets.mine.push(def);
      }
    }
    return buckets;
  }, [definitions, search, callerEmail]);

  const totalMatches = grouped.system.length + grouped.office.length + grouped.mine.length;

  const toggleDefinition = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Any change in selection invalidates the prereqs step's checks — reset
    // so the user re-confirms against the new (possibly different) list.
    setCheckedPrereqs(new Set());
  };

  const resetForm = () => {
    setSelected(new Set());
    setSubmitError(null);
    setSearch("");
    setStep("select");
    setCheckedPrereqs(new Set());
  };

  // Selected definitions in a stable order (order of insertion into `selected`).
  const selectedDefinitions = useMemo(
    () => {
      const byId = new Map(definitions.map((d) => [d.id, d]));
      return Array.from(selected).map((id) => byId.get(id)).filter((d): d is TaskDefinitionDto => !!d);
    },
    [definitions, selected]
  );

  // Aggregate prereqs across every selected agent, deduped by lowercased+trimmed
  // title. Description on a merged prereq is the first non-empty one seen
  // (agents that share a prereq generally share the wording too). `agentNames`
  // gives the "required for X, Y" hint under each checkbox.
  interface AggregatedPrereq {
    key: string;
    title: string;
    description?: string;
    agentNames: string[];
  }
  const aggregatedPrereqs = useMemo<AggregatedPrereq[]>(() => {
    const map = new Map<string, AggregatedPrereq>();
    for (const def of selectedDefinitions) {
      for (const p of def.prerequisites ?? []) {
        const title = p.title?.trim();
        if (!title) continue;
        const key = title.toLowerCase();
        const existing = map.get(key);
        if (existing) {
          if (!existing.agentNames.includes(def.name)) {
            existing.agentNames.push(def.name);
          }
          if (!existing.description && p.description) {
            existing.description = p.description;
          }
        } else {
          map.set(key, {
            key,
            title,
            description: p.description,
            agentNames: [def.name],
          });
        }
      }
    }
    return Array.from(map.values());
  }, [selectedDefinitions]);

  const allPrereqsChecked = aggregatedPrereqs.every((p) => checkedPrereqs.has(p.key));

  const togglePrereq = (key: string) => {
    setCheckedPrereqs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Step navigation. The prereqs step is skipped in both directions when the
  // aggregated list is empty, so a selection with no prereqs at all goes
  // straight select → confirm and vice versa on Back.
  const handleNext = () => {
    if (step === "select") {
      setStep(aggregatedPrereqs.length > 0 ? "prereqs" : "confirm");
    } else if (step === "prereqs") {
      setStep("confirm");
    }
  };

  const handleBack = () => {
    if (step === "confirm") {
      setStep(aggregatedPrereqs.length > 0 ? "prereqs" : "select");
    } else if (step === "prereqs") {
      setStep("select");
    }
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Snapshot upload is Excel-only (reads the .xlsx via Office.context.
      // document.getFileAsync). In standalone we skip it and let the backend
      // fall back to the most-recent snapshot it already has — agents run
      // against the last-uploaded version, which is usually good enough.
      if (!isStandalone) {
        await uploadWorkbookSnapshot(spreadsheetId);
      }

      const byId = new Map(definitions.map((d) => [d.id, d]));
      const request: CreateTasksRequest = {
        spreadsheetId,
        items: Array.from(selected).map((definitionId) => ({
          name: byId.get(definitionId)?.name ?? definitionId,
          taskDefinitionId: definitionId,
        })),
      };

      await createTasks(request);
      // Emit one event per task that was created (selected can be 1..N).
      // Property carries the task definition id so we can slice by which
      // template the user runs; outputType comes from the definition.
      for (const definitionId of selected) {
        const def = byId.get(definitionId);
        track("task.created", {
          taskDefinitionId: definitionId,
          outputType: def?.outputType,
        });
      }
      onSuccess();
      handleClose();
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onDismiss();
  };

  const renderRow = (def: TaskDefinitionDto) => {
    const isSelected = selected.has(def.id);
    return (
      <div
        key={def.id}
        // Whole card toggles — bigger hit area than just the checkbox.
        className={`${styles.agentCard} ${isSelected ? styles.agentCardSelected : ""}`}
        onClick={() => toggleDefinition(def.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleDefinition(def.id);
          }
        }}
        aria-pressed={isSelected}
      >
        <Checkbox
          className={styles.agentCardCheckbox}
          checked={isSelected}
          // The wrapping card already toggles; suppress the checkbox's own
          // onChange to avoid double-toggle when clicking the box itself.
          onChange={(e) => {
            e.stopPropagation();
            toggleDefinition(def.id);
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <div className={styles.agentCardBody}>
          <div className={styles.agentCardHeader}>
            <Text className={styles.agentCardName}>{def.name}</Text>
            {def.category && (
              <Badge
                className={styles.categoryBadge}
                appearance="tint"
                color="informative"
                size="small"
              >
                {def.category}
              </Badge>
            )}
          </div>
          {def.description ? (
            <Text className={`${typography.bodySmall} ${styles.agentCardDescription}`}>
              {def.description}
            </Text>
          ) : (
            <Text className={`${typography.bodySmall} ${styles.agentCardDescriptionEmpty}`}>
              No description
            </Text>
          )}
        </div>
      </div>
    );
  };

  // Sections that actually have content, in the canonical My → Office → System
  // display order. Empty sections are dropped entirely rather than rendered
  // with a "(0)" header — saves vertical space.
  const nonEmptySections = VISIBILITY_ORDER
    .map((s) => ({ ...s, agents: grouped[s.key] }))
    .filter((s) => s.agents.length > 0);

  // Step indicator text for the title. Total steps is 3 when there are
  // prereqs to show, 2 when the checklist step is skipped entirely.
  const totalSteps = aggregatedPrereqs.length > 0 ? 3 : 2;
  const currentStepNumber =
    step === "select" ? 1
      : step === "prereqs" ? 2
      : (aggregatedPrereqs.length > 0 ? 3 : 2);

  const titleForStep = (): string => {
    switch (step) {
      case "select": return "Run Agents — Select";
      case "prereqs": return "Run Agents — Prerequisites";
      case "confirm": return "Run Agents — Confirm";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            {titleForStep()}
            <Text className={typography.caption} style={{ display: "block", fontWeight: "normal" }}>
              Step {currentStepNumber} of {totalSteps}
            </Text>
          </DialogTitle>

          <DialogContent>
            {submitError && (
              <div className={styles.errorMessage}>
                <Text weight="semibold">Error creating agents</Text>
                <br />
                <Text>{submitError}</Text>
              </div>
            )}

            {step === "select" && (
              <>
                <div className={styles.searchRow}>
                  <Input
                    value={search}
                    onChange={(_e, data) => setSearch(data.value)}
                    placeholder="Search agents..."
                    contentBefore={<Search20Regular />}
                    contentAfter={
                      search ? (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<Dismiss24Regular />}
                          onClick={() => setSearch("")}
                          aria-label="Clear search"
                        />
                      ) : undefined
                    }
                  />
                </div>

                {isLoading ? (
                  <div className={styles.loading}>
                    <Spinner size="small" label="Loading agents..." />
                  </div>
                ) : loadError ? (
                  <div className={styles.errorMessage}>
                    <Text>{loadError}</Text>
                  </div>
                ) : definitions.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Text className={typography.emptyStateBody}>
                      No agents are available yet. Create one in the agent management app
                      or ask your office to share one with you.
                    </Text>
                  </div>
                ) : search.trim() && totalMatches === 0 ? (
                  <div className={styles.emptyState}>
                    <Text className={typography.emptyStateBody}>
                      No agents match &ldquo;{search.trim()}&rdquo;.
                    </Text>
                  </div>
                ) : (
                  <div className={styles.resultsArea}>
                    {nonEmptySections.map((section) => (
                      <div key={section.key}>
                        <div className={styles.sectionHeader}>
                          <Text className={typography.subsectionTitle}>{section.label}</Text>
                          <Text className={typography.caption}>{section.agents.length}</Text>
                        </div>
                        <div className={styles.sectionAgentList}>
                          {section.agents.map(renderRow)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {step === "prereqs" && (
              <>
                <Text className={styles.stepIntro}>
                  Before running, confirm each item below is in place. Skipping any
                  of these usually means the agent will fail or produce incomplete
                  results.
                </Text>
                <div className={styles.prereqList}>
                  {aggregatedPrereqs.map((p) => (
                    <div key={p.key} className={styles.prereqItem}>
                      <div className={styles.prereqCheckboxRow}>
                        <Checkbox
                          checked={checkedPrereqs.has(p.key)}
                          onChange={() => togglePrereq(p.key)}
                          label={<Text weight="semibold">{p.title}</Text>}
                        />
                      </div>
                      {p.description && (
                        <Text className={styles.prereqDescription}>{p.description}</Text>
                      )}
                      {p.agentNames.length > 1 && (
                        <Text className={styles.prereqAppliesTo}>
                          Required for: {p.agentNames.join(", ")}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {step === "confirm" && (
              <>
                <Text className={styles.stepIntro}>
                  About to run {selectedDefinitions.length} agent{selectedDefinitions.length !== 1 ? "s" : ""}.
                  Review below and click Run to submit.
                </Text>
                <div className={styles.confirmList}>
                  {selectedDefinitions.map((def) => (
                    <div key={def.id} className={styles.confirmRow}>
                      <Text weight="semibold">{def.name}</Text>
                      {def.description && (
                        <Text className={`${typography.caption} ${styles.confirmRowDescription}`}>
                          {def.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            {step === "select" && (
              <>
                <Text className={typography.caption}>
                  {selected.size} agent{selected.size !== 1 ? "s" : ""} selected
                </Text>
                <Button appearance="secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleNext}
                  disabled={selected.size === 0}
                >
                  Next
                </Button>
              </>
            )}
            {step === "prereqs" && (
              <>
                <Text className={typography.caption}>
                  {[...checkedPrereqs].filter((k) => aggregatedPrereqs.some((p) => p.key === k)).length}{" / "}
                  {aggregatedPrereqs.length} checked
                </Text>
                <Button appearance="secondary" onClick={handleBack} disabled={isSubmitting}>
                  Back
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleNext}
                  disabled={!allPrereqsChecked}
                >
                  Continue
                </Button>
              </>
            )}
            {step === "confirm" && (
              <>
                <Button appearance="secondary" onClick={handleBack} disabled={isSubmitting}>
                  Back
                </Button>
                <Button appearance="secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || selected.size === 0}
                >
                  {isSubmitting
                    ? "Creating..."
                    : `Run ${selected.size} Agent${selected.size !== 1 ? "s" : ""}`}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

// Re-export the type so other files can pull it from here if convenient,
// even though this component doesn't currently use the type as a value.
export type { TaskDefinitionVisibility };

export default CreateTaskDialog;
